import { useCallback, useRef } from 'react';

const OFFSET = 12;

/**
 * Clamps a tooltip edge so the element stays within the viewport.
 *
 * @param pos   - The pointer coordinate (clientX or clientY).
 * @param size  - The tooltip dimension (width or height) on that axis.
 * @param limit - The viewport dimension (innerWidth or innerHeight).
 */
const clampEdge = (pos: number, size: number, limit: number) => {
  const max = limit - size - OFFSET;
  return Math.max(OFFSET, Math.min(pos + OFFSET, max));
};

/**
 * Hook that returns a ref and an imperative `updatePosition` callback
 * for positioning an `SvgTooltip` without triggering React re-renders.
 *
 * @example
 * ```tsx
 * const { tooltipRef, updatePosition } = useSvgTooltip();
 *
 * const handlePointerMove = (e: PointerEvent) => {
 *   updatePosition(e.clientX, e.clientY);
 * };
 *
 * <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
 *   {hoveredItem?.name}
 * </SvgTooltip>
 * ```
 */
const useSvgTooltip = () => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  /** Imperatively moves the tooltip to the given viewport coordinates, clamped to the viewport. */
  const updatePosition = useCallback((x: number, y: number) => {
    const el = tooltipRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    el.style.left = `${clampEdge(x, rect.width, window.innerWidth)}px`;
    el.style.top = `${clampEdge(y, rect.height, window.innerHeight)}px`;
  }, []);

  return { tooltipRef, updatePosition } as const;
};

export { useSvgTooltip };
